import { Loader2, MessageCircle, Sparkles, Users, Video } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * What Channels shows when no channel is open. It was one grey sentence
 * ("No teams yet — create one from the sidebar.") on an empty white page;
 * now a greeting and the three things you'd come here to do.
 */
export function WelcomePane({ name, hasTeams, busy, onCreateTeam, onStartChat, onMeetNow }: {
    name: string;
    hasTeams: boolean;
    busy?: boolean;
    onCreateTeam: () => void;
    onStartChat: () => void;
    onMeetNow: () => void;
}) {
    const first = name.split(/[\s@]/)[0] || "there";
    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

    return (
        <div className="relative flex h-full min-h-0 flex-1 items-center justify-center overflow-y-auto p-6">
            <div aria-hidden className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
            <div aria-hidden className="pointer-events-none absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-fuchsia-400/15 blur-3xl" />
            <div className="relative w-full max-w-2xl text-center">
                <Illustration />
                <p className="mt-6 text-sm font-medium text-primary">{greeting}, {first} 👋</p>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight">
                    {hasTeams ? "Pick a channel to jump in" : "Bring your team together"}
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                    {hasTeams
                        ? "Channels keep a topic's conversations, files and meetings in one place."
                        : "Create a team for your group, then add a channel for each project or topic."}
                </p>
                <div className="mt-7 grid gap-3 text-left sm:grid-cols-3">
                    <ActionCard tone="bg-violet-600" icon={<Users className="h-5 w-5" />} title="Create a team" text="Channels for every project" onClick={onCreateTeam} />
                    <ActionCard tone="bg-sky-600" icon={<MessageCircle className="h-5 w-5" />} title="Start a chat" text="One-to-one or a group" onClick={onStartChat} />
                    <ActionCard
                        tone="bg-emerald-600"
                        icon={busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Video className="h-5 w-5" />}
                        title="Connect now"
                        text="Start a video meeting and share the link"
                        onClick={onMeetNow}
                        disabled={busy}
                    />
                </div>
            </div>
        </div>
    );
}

function ActionCard({ tone, icon, title, text, onClick, disabled }: {
    tone: string;
    icon: React.ReactNode;
    title: string;
    text: string;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="group flex items-start gap-3 rounded-xl border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60"
        >
            <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white shadow-sm", tone)}>{icon}</span>
            <span className="min-w-0">
                <span className="block text-sm font-semibold group-hover:text-primary">{title}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{text}</span>
            </span>
        </button>
    );
}

/** A small cluster of coloured bubbles — people, a chat and a call. */
function Illustration() {
    return (
        <div aria-hidden className="relative mx-auto h-28 w-44">
            <span className="absolute left-2 top-6 flex h-16 w-16 -rotate-6 items-center justify-center rounded-2xl bg-sky-500 text-white shadow-lg">
                <MessageCircle className="h-8 w-8" />
            </span>
            <span className="absolute left-14 top-0 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary text-white shadow-xl">
                <Users className="h-10 w-10" />
            </span>
            <span className="absolute right-1 top-8 flex h-14 w-14 rotate-6 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg">
                <Video className="h-7 w-7" />
            </span>
            <span className="absolute right-10 top-0 flex h-7 w-7 animate-pulse items-center justify-center rounded-full bg-amber-400 text-white shadow">
                <Sparkles className="h-4 w-4" />
            </span>
        </div>
    );
}
